import type {
  CorrectionBody,
  Page,
  RecordApiKind,
  RecordByKind,
  RecordDetail,
  RecordHistory,
  RecordListQuery,
  ReleaseBody,
  ReviewAction,
  ReviewActionBody,
} from '@fruuit/shared'
import { type QueryKey, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { api, type QueryParams } from '../../api/client'
import { type ApiQueryOptions, useApiQuery } from '../../api/hooks'

/**
 * Typed client for the lane-B records API (`/api/v1/records/:kind`). Every lane can reuse
 * these helpers and hooks; the server enforces everything (P-UI-003).
 */

export interface ClientOrg {
  id: string
  name: string
}

export interface StaffMember {
  id: string
  name: string
  roles: ('manager' | 'reviewer')[]
}

const path = (kind: RecordApiKind, id?: string, action?: string) =>
  `/records/${kind}${id ? `/${encodeURIComponent(id)}` : ''}${action ? `/${action}` : ''}`

interface Ctx {
  requestId: string
}

export const recordsApi = {
  list: <K extends RecordApiKind>(kind: K, query: RecordListQuery = {}, signal?: AbortSignal) =>
    api.get<Page<RecordByKind[K]>>(path(kind), {
      query: query as QueryParams,
      ...(signal ? { signal } : {}),
    }),
  get: <K extends RecordApiKind>(kind: K, id: string, signal?: AbortSignal) =>
    api.get<RecordDetail<K>>(path(kind, id), signal ? { signal } : undefined),
  history: (kind: RecordApiKind, id: string, signal?: AbortSignal) =>
    api.get<RecordHistory>(path(kind, id, 'history'), signal ? { signal } : undefined),
  create: <K extends RecordApiKind>(kind: K, body: unknown, ctx: Ctx) =>
    api.post<RecordByKind[K]>(path(kind), body, { requestId: ctx.requestId }),
  update: <K extends RecordApiKind>(kind: K, id: string, body: unknown, ctx: Ctx) =>
    api.patch<RecordByKind[K]>(path(kind, id), body, { requestId: ctx.requestId }),
  review: <K extends RecordApiKind>(
    kind: K,
    id: string,
    action: ReviewAction,
    body: ReviewActionBody,
    ctx: Ctx,
  ) => api.post<RecordByKind[K]>(path(kind, id, action), body, { requestId: ctx.requestId }),
  release: <K extends RecordApiKind>(kind: K, id: string, body: ReleaseBody, ctx: Ctx) =>
    api.post<RecordByKind[K]>(path(kind, id, 'release'), body, { requestId: ctx.requestId }),
  correct: <K extends RecordApiKind>(kind: K, id: string, body: CorrectionBody, ctx: Ctx) =>
    api.post<RecordByKind[K]>(path(kind, id, 'correct'), body, { requestId: ctx.requestId }),
  clients: (signal?: AbortSignal) =>
    api.get<{ items: ClientOrg[] }>('/review/clients', signal ? { signal } : undefined),
  staff: (signal?: AbortSignal) =>
    api.get<{ items: StaffMember[] }>('/review/staff', signal ? { signal } : undefined),
}

/** Query keys: everything under `['records', kind]` is invalidated after a mutation. */
export const recordKeys = {
  all: ['records'] as const,
  kind: (kind: RecordApiKind) => ['records', kind] as const,
  list: (kind: RecordApiKind, query: RecordListQuery) => ['records', kind, 'list', query] as const,
  detail: (kind: RecordApiKind, id: string) => ['records', kind, 'detail', id] as const,
  history: (kind: RecordApiKind, id: string) => ['records', kind, 'history', id] as const,
  clients: ['review', 'clients'] as const,
  staff: ['review', 'staff'] as const,
}

function cleanQuery(query: RecordListQuery): QueryParams {
  const out: QueryParams = {}
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') out[key] = value as string | number
  }
  return out
}

/** `GET /records/:kind` (paginated). */
export function useRecordList<K extends RecordApiKind>(
  kind: K,
  query: RecordListQuery = {},
  options: Omit<ApiQueryOptions<Page<RecordByKind[K]>>, 'query'> = {},
) {
  return useApiQuery<Page<RecordByKind[K]>>(recordKeys.list(kind, query), path(kind), {
    query: cleanQuery(query),
    placeholderData: (previous) => previous,
    ...options,
  })
}

/** `GET /records/:kind/:id` with contributors, people and the caller's actions. */
export function useRecord<K extends RecordApiKind>(
  kind: K,
  id: string | null | undefined,
  options: Omit<ApiQueryOptions<RecordDetail<K>>, 'query'> = {},
) {
  return useApiQuery<RecordDetail<K>>(recordKeys.detail(kind, id ?? ''), path(kind, id ?? '-'), {
    enabled: Boolean(id),
    ...options,
  })
}

/** `GET /records/:kind/:id/history`: version chain and review events. */
export function useRecordHistory(kind: RecordApiKind, id: string | null | undefined) {
  return useApiQuery<RecordHistory>(
    recordKeys.history(kind, id ?? ''),
    path(kind, id ?? '-', 'history'),
    { enabled: Boolean(id) },
  )
}

/** Active investor orgs for client-context selects (R-06). */
export function useClients() {
  return useApiQuery<{ items: ClientOrg[] }>(recordKeys.clients, '/review/clients', {
    staleTime: 5 * 60_000,
  })
}

/** Fruuit staff for manager, reviewer and owner selects (VAL-043, VAL-064). */
export function useStaff() {
  return useApiQuery<{ items: StaffMember[] }>(recordKeys.staff, '/review/staff', {
    staleTime: 5 * 60_000,
  })
}

/** Invalidate every records, review-queue and audit query after a change. */
export function useInvalidateRecords() {
  const queryClient = useQueryClient()
  return useCallback(
    async (extra: QueryKey[] = []) => {
      await Promise.all(
        [recordKeys.all, ['review'], ['audit'], ...extra].map((queryKey) =>
          queryClient.invalidateQueries({ queryKey }),
        ),
      )
    },
    [queryClient],
  )
}
